import { useContext, useEffect, useState } from "react";
import { createContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

interface LocationContextType {
  address: string | null;
  street: string | null;
  streetNumber: string | null;
  hasLocation: boolean;
  setLocation: (address: string, street: string, streetNumber: string) => void;
  locate: () => Promise<{
    address: string;
    street: string;
    streetNumber: string;
  }>;
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined
);

export const LocationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [address, setAddress] = useState<string | null>(null);
  const [street, setStreet] = useState<string | null>(null);
  const [streetNumber, setStreetNumber] = useState<string | null>(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    const address = localStorage.getItem("address");
    const street = localStorage.getItem("street");
    const streetNumber = localStorage.getItem("streetNumber");
    setAddress(address);
    setStreet(street);
    setStreetNumber(streetNumber);
  }, []);

  const setLocation = (
    address: string,
    street: string,
    streetNumber: string
  ) => {
    setAddress(address);
    setStreet(street);
    setStreetNumber(streetNumber);

    localStorage.setItem("address", address);
    localStorage.setItem("street", street);
    localStorage.setItem("streetNumber", streetNumber);
  };

  const getCurrentPosition = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
  };

  const locate = async () => {
    if (!navigator.geolocation) {
      return { address: "", street: "", streetNumber: "" };
    }

    const position = await getCurrentPosition();
    const { latitude, longitude } = position.coords;
    let address = "";
    let street = "";
    let streetNumber = "";

    const res = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${
        import.meta.env.VITE_GOOGLE_MAPS_API_KEY
      }&language=${i18n.language}`
    );

    const { results } = res.data;
    const components = results[0].address_components;
    address = results[0].formatted_address;

    for (const component of components) {
      if (component.types.includes("route")) {
        street = component.long_name;
      }
      if (component.types.includes("street_number")) {
        streetNumber = component.long_name;
      }
    }
    return { address, street, streetNumber };
  };

  return (
    <LocationContext.Provider
      value={{
        address,
        street,
        streetNumber,
        setLocation,
        hasLocation:
          address !== null && street !== null && streetNumber !== null,
        locate: locate,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};
